import { Capacitor } from '@capacitor/core';
import { useState } from 'react';

import { Button } from '@/components/Button';
import { useAuth } from '@/store/auth/auth.store';

import OtpInput from '../OtpCode/OtpInput';

import RecoveryCodes from './RecoveryCodes';
import s from './TotpEnroll.module.scss';

type TotpEnrollProps = {
  /** QR data-URL from `enableTotp()`. */
  qrCode: string;
  /** otpauth:// URL — clicking the QR code opens this (deep-links into authenticator apps). */
  url: string;
  /**
   * Fired once the code is accepted, before any recovery codes are shown. Use it to
   * refresh status or set TOTP as the default method. Receives the recovery codes
   * (empty unless this is the account's first confirmed method).
   */
  onConfirmed?: (recoveryCodes: string[]) => void | Promise<void>;
  /**
   * Fired when the user finishes — immediately when there are no recovery codes,
   * otherwise after they acknowledge them.
   */
  onDone: () => void;
};

/**
 * Authenticator-app enrolment, host-agnostic so the Security settings modal and the
 * sign-up flow share one screen. Enabling TOTP only arms it server-side — it stays
 * pending until this confirm step proves possession of the app. On the account's
 * first-ever confirmed method the API hands back recovery codes, shown once here.
 */
const TotpEnroll = ({ qrCode, url, onConfirmed, onDone }: TotpEnrollProps) => {
  const [code, setCode] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [copied, setCopied] = useState(false);

  const ready = code.join('').length === 6;

  // Extract the base32 secret for manual entry fallback
  const secret = (() => {
    try {
      return new URL(url).searchParams.get('secret') ?? '';
    } catch {
      return '';
    }
  })();
  const formattedSecret = secret.match(/.{1,4}/g)?.join(' ') ?? secret;

  const copySecret = async () => {
    await navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const confirm = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError('');
    try {
      const codes = await useAuth.getState().confirmTwoFactorMethod('totp', code.join(''));
      await onConfirmed?.(codes);
      if (codes.length)
        setRecoveryCodes(codes); // show once, then onDone
      else onDone();
    } catch (err) {
      console.error('Failed to confirm authenticator app:', err);
      setError('That code didn’t match. Please try the latest one from your app.');
    } finally {
      setBusy(false);
    }
  };

  if (recoveryCodes) {
    return <RecoveryCodes codes={recoveryCodes} onDone={onDone} />;
  }

  return (
    <div className={s.body}>
      <div className={s.heading}>
        <h2 className={s.title}>Set Up Authenticator App</h2>
      </div>

      <ol className={s.steps}>
        <li className={s.step}>
          <span className={s.stepNum}>1</span>
          <div>
            <strong className={s.stepTitle}>Install Authenticator App</strong>
            <p>
              Any TOTP-compatible app works - popular choices include{' '}
              <strong>Google Authenticator</strong>, <strong>Authy</strong>,{' '}
              <strong>Microsoft Authenticator</strong>, <strong>1Password</strong>, and{' '}
              <strong>Bitwarden Authenticator</strong>.
            </p>
          </div>
        </li>
        <li className={s.step}>
          <span className={s.stepNum}>2</span>
          <div>
            <strong className={s.stepTitle}>Scan QR Code</strong>
            <p>
              Please open your app, tap &ldquo;Add account&rdquo; or the&nbsp;+ icon, then point
              your camera at the code below.
            </p>
          </div>
        </li>
      </ol>

      {Capacitor.getPlatform() === 'android' ? (
        <>
          <a
            href={url}
            onClick={(e) => {
              e.preventDefault();
              window.open(url, '_system');
            }}
          >
            <img src={qrCode} alt="Two-factor authentication QR code" className={s.qr} />
          </a>
          <p className={s.qrHint}>You can also tap the code to open your authenticator app.</p>
        </>
      ) : (
        <img src={qrCode} alt="Two-factor authentication QR code" className={s.qr} />
      )}

      {secret && (
        <div className={s.codeBox}>
          <p className={s.stepTitle} style={{ marginBottom: 8 }}>
            Can&apos;t scan? Enter key manually
          </p>
          <p
            className={s.codeItem}
            style={{ fontFamily: 'monospace', wordBreak: 'break-all', marginBottom: 8 }}
          >
            {formattedSecret}
          </p>
          <button type="button" className={s.copyBtn} onClick={copySecret}>
            {copied ? 'Copied' : 'Copy Key'}
          </button>
        </div>
      )}

      <ol className={s.steps} start={3}>
        <li className={s.step}>
          <span className={s.stepNum}>3</span>
          <div>
            <strong className={s.stepTitle}>Enter 6-Digit Code</strong>
            <p>
              Your app refreshes the code every 30 seconds. Please type the current one below to
              confirm setup.
            </p>
          </div>
        </li>
      </ol>

      <OtpInput value={code} onChange={setCode} onUserInput={() => setError('')} />
      {error && <p className={s.error}>{error}</p>}
      <Button className={s.submit} onClick={confirm} disabled={!ready || busy}>
        {busy ? 'Verifying…' : 'Confirm'}
      </Button>
    </div>
  );
};

export default TotpEnroll;
